import { useEffect, useRef, useState } from "react";
import { api, formatApiError, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Play, Image as ImageIcon, Upload, Film } from "lucide-react";
import ImageInput from "@/components/ImageInput";
import { getYouTubeThumb } from "@/lib/media";

const emptyDefaults = { type: "photo", media_url: "", caption: "", order_index: 0 };

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/media");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    if (!form.media_url) {
      toast.error("Please upload a photo or paste a video URL.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type: form.type,
        media_url: form.media_url,
        caption: form.caption || "",
        order_index: Number(form.order_index) || 0,
      };
      if (editing && editing.id) {
        await api.put(`/media/${editing.id}`, payload);
        toast.success("Media updated.");
      } else {
        await api.post("/media", payload);
        toast.success("Media added.");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-media">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="overline mb-3">// Manage</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Gallery</h1>
          <p className="text-voltage-muted text-sm mt-2 font-mono">Photos & videos for the public gallery.</p>
        </div>
        <button
          onClick={() => setEditing({ ...emptyDefaults })}
          data-testid="media-add"
          className="btn-primary clip-bolt"
        >
          <Plus className="w-4 h-4" /> New Media
        </button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-voltage-blue/40 p-12 text-center text-voltage-muted">
          No media yet. Add your first photo or video.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {items.map((m) => (
            <MediaCard key={m.id} item={m} onEdit={() => setEditing(m)} onDelete={() => remove(m.id)} />
          ))}
        </div>
      )}

      {editing !== null && (
        <MediaModal
          initial={editing}
          onSave={save}
          onClose={() => setEditing(null)}
          loading={loading}
        />
      )}
    </div>
  );
}

function MediaCard({ item, onEdit, onDelete }) {
  const isPhoto = item.type === "photo";
  const ytThumb = !isPhoto ? getYouTubeThumb(item.media_url) : null;

  return (
    <div className="relative group border border-voltage-blue/30 bg-black aspect-square overflow-hidden" data-testid={`media-card-${item.id}`}>
      {isPhoto ? (
        <img src={item.media_url} alt={item.caption} className="w-full h-full object-cover" />
      ) : ytThumb ? (
        <img src={ytThumb} alt={item.caption} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-voltage-surface">
          <Play className="w-8 h-8 text-voltage-yellow" fill="#F5C518" />
        </div>
      )}
      <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 text-[10px] font-mono uppercase tracking-widest text-voltage-yellow flex items-center gap-1">
        {isPhoto ? <ImageIcon className="w-3 h-3" /> : <Play className="w-3 h-3" fill="currentColor" />}
        {item.type}
      </div>
      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
        <p className="text-white text-xs text-center line-clamp-3 font-mono">{item.caption || "(no caption)"}</p>
        <div className="flex gap-2 mt-2">
          <button
            onClick={onEdit}
            data-testid={`media-edit-${item.id}`}
            className="p-2 bg-voltage-blue text-white hover:bg-voltage-blue-hover"
            aria-label="Edit"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            data-testid={`media-delete-${item.id}`}
            className="p-2 bg-red-500 text-white hover:bg-red-600"
            aria-label="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function MediaModal({ initial, onSave, onClose, loading }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const cleaned = { ...form };
    delete cleaned.id;
    delete cleaned.created_at;
    onSave(cleaned);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="media-modal">
      <form onSubmit={submit} className="voltage-card w-full max-w-2xl p-8 my-8 relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-voltage-muted hover:text-white"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-8">
          {initial.id ? "Edit Media" : "New Media"}
        </h2>

        <div className="space-y-6">
          <div>
            <label className="overline mb-2 block">Type</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "photo", label: "Photo", icon: ImageIcon },
                { v: "video", label: "Video", icon: Play },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => set("type", o.v)}
                  data-testid={`media-type-${o.v}`}
                  className={`flex items-center justify-center gap-2 py-3 border-2 font-display font-bold uppercase tracking-widest text-xs transition-colors ${
                    form.type === o.v
                      ? "border-voltage-yellow bg-voltage-yellow/10 text-voltage-yellow"
                      : "border-voltage-blue/40 text-voltage-muted hover:border-voltage-blue"
                  }`}
                >
                  <o.icon className="w-4 h-4" /> {o.label}
                </button>
              ))}
            </div>
          </div>

          {form.type === "photo" ? (
            <div>
              <label className="overline mb-2 block">Photo</label>
              <ImageInput
                value={form.media_url}
                onChange={(v) => set("media_url", v)}
                testid="media-photo"
                maxWidth={1400}
                quality={0.85}
              />
            </div>
          ) : (
            <VideoInput
              value={form.media_url}
              onChange={(v) => set("media_url", v)}
            />
          )}

          <div>
            <label className="overline mb-2 block">Caption</label>
            <input
              type="text"
              value={form.caption || ""}
              onChange={(e) => set("caption", e.target.value)}
              data-testid="media-caption"
              placeholder="e.g. Regionals 2026 — driver stand"
              className="w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-body"
            />
          </div>

          <div>
            <label className="overline mb-2 block">Order Index</label>
            <input
              type="number"
              value={form.order_index ?? 0}
              onChange={(e) => set("order_index", Number(e.target.value))}
              data-testid="media-order"
              className="w-32 bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-mono"
            />
            <p className="text-xs text-voltage-muted font-mono mt-1">Lower = shown first.</p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="media-cancel">Cancel</button>
          <button type="submit" disabled={loading} data-testid="media-save" className="btn-primary clip-bolt disabled:opacity-50">
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function VideoInput({ value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUrl, setShowUrl] = useState(false);
  const isUploaded = typeof value === "string" && value.startsWith("/api/media/file/");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please choose a video file (MP4, MOV, WEBM…).");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video is too large (max 200MB). Try a shorter clip or upload to YouTube.");
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const url = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/media/upload-video`);
        const token = getToken();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) resolve(data.url);
            else reject(new Error(data.detail || "Upload failed"));
          } catch (err) {
            reject(err);
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        const fd = new FormData();
        fd.append("file", file);
        xhr.send(fd);
      });
      onChange(url);
      toast.success("Video uploaded.");
    } catch (e) {
      toast.error(e.message || "Upload failed.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const clear = () => {
    onChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const streamUrl = isUploaded && value.startsWith("/") ? `${API.replace(/\/api$/, "")}${value}` : value;

  return (
    <div>
      <label className="overline mb-2 block">Video</label>

      {value && !uploading ? (
        <div className="relative inline-block max-w-full">
          {isUploaded ? (
            <video src={streamUrl} className="max-h-56 max-w-full bg-black border border-voltage-blue/40" controls preload="metadata" />
          ) : (
            <div className="border border-voltage-blue/40 p-4 bg-black/40 max-w-lg break-all font-mono text-xs text-voltage-muted">
              {value}
            </div>
          )}
          <button
            type="button"
            onClick={clear}
            data-testid="media-video-clear"
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 hover:bg-red-600"
            aria-label="Remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : uploading ? (
        <div className="border-2 border-dashed border-voltage-yellow/60 p-6 text-center">
          <div className="font-mono text-sm text-voltage-yellow mb-3">Uploading… {progress}%</div>
          <div className="w-full h-2 bg-voltage-surface border border-voltage-blue/40 overflow-hidden">
            <div
              className="h-full bg-voltage-yellow transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <div
          onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
          data-testid="media-video-dropzone"
          className="border-2 border-dashed border-voltage-blue/40 hover:border-voltage-yellow transition-colors p-6 flex flex-col items-center justify-center gap-2 text-center cursor-pointer"
        >
          <Film className="w-8 h-8 text-voltage-muted" />
          <div className="text-sm text-voltage-muted">Drop a video here or click to choose</div>
          <div className="text-xs text-voltage-muted font-mono">MP4 · MOV · WEBM — max 200MB</div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-2 text-xs">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="media-video-pick"
          className="inline-flex items-center gap-1.5 text-voltage-yellow hover:text-white font-display font-bold uppercase tracking-widest disabled:opacity-50"
        >
          <Upload className="w-3.5 h-3.5" /> {value ? "Replace" : "Upload"}
        </button>
        <span className="text-voltage-muted">·</span>
        <button
          type="button"
          onClick={() => setShowUrl((s) => !s)}
          data-testid="media-video-toggle-url"
          className="text-voltage-muted hover:text-white font-display font-bold uppercase tracking-widest"
        >
          {showUrl ? "Hide URL field" : "Or paste a YouTube/Vimeo link"}
        </button>
      </div>

      {showUrl && (
        <input
          type="url"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="media-video-url"
          placeholder="https://youtu.be/… or https://vimeo.com/…"
          className="mt-2 w-full bg-black/50 border border-voltage-blue/40 focus:border-voltage-yellow outline-none px-3 py-2 text-white font-mono text-sm"
        />
      )}

      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

